import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useCertificate } from '../../contexts/CertificateContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  Download, Share2, ArrowLeft, Award, Calendar,
  User, BookOpen, Copy, Check, Shield, Printer, AlertCircle,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';

const SETTING_DEFAULTS = {
  org_name: 'INNOVACTIVE 360°',
  cert_title: 'Certificat de Complétion',
  cert_subtitle: 'Ceci certifie que',
  cert_body: 'a réussi avec succès le cours',
  signatory_name: 'Innovactive 360°',
  signatory_title: 'Directeur pédagogique',
  footer_note: '',
  logo_url: '',
  signatory_signature_url: '',
  show_score: true,
  show_modules: true,
};

const CertificateViewer = () => {
  const { certificateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getCertificateById, shareCertificate } = useCertificate();
  const printRef = useRef(null);

  const [certificate, setCertificate] = useState(null);
  const [settings, setSettings]       = useState(SETTING_DEFAULTS);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [copied, setCopied]           = useState(false);

  const handlePrint = () => {
    const style = document.createElement('style');
    style.id = 'certificate-print-only';
    style.textContent = `
      @media print {
        body > * { visibility: hidden !important; }
        #certificate-zone { visibility: visible !important; position: fixed !important; inset: 0; margin: 0; padding: 16px; box-sizing: border-box; }
        #certificate-zone * { visibility: visible !important; }
      }
    `;
    document.head.appendChild(style);
    window.addEventListener('afterprint', () => {
      const el = document.getElementById('certificate-print-only');
      if (el) el.remove();
    }, { once: true });
    window.print();
  };

  useEffect(() => {
    if (!certificateId) return;
    setLoading(true);
    setError(null);
    Promise.all([
      getCertificateById(certificateId),
      axios.get('/api/platform-settings').catch(() => ({ data: null })),
    ]).then(([certResult, settingsRes]) => {
      if (certResult.success) {
        setCertificate(certResult.certificate);
        if (searchParams.get('print') === '1') setTimeout(() => handlePrint(), 800);
      } else {
        setError(certResult.error || 'Certificat introuvable');
      }
      if (settingsRes.data?.success) {
        setSettings({ ...SETTING_DEFAULTS, ...settingsRes.data.settings });
      }
      setLoading(false);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateId, getCertificateById, searchParams]);

  const formatDate = dateString => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(certificate.verification_code);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error('Impossible de copier'); }
  };

  const handleShare = async (platform = 'linkedin') => {
    const result = await shareCertificate(certificate.id, platform);
    if (result.success) { window.open(result.shareLink, '_blank'); toast.success('Lien de partage ouvert !'); }
    else toast.error(result.error || 'Erreur partage');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Chargement du certificat…</p>
        </div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{error || 'Certificat introuvable'}</h1>
          <button onClick={() => navigate('/certificates')} className="mt-4 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90">
            Mes certificats
          </button>
        </div>
      </div>
    );
  }

  const courseTitle     = certificate.course?.title || certificate.metadata?.course_title || 'Cours';
  const holderName      = certificate.holder?.full_name
    || (user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : null)
    || user?.email
    || 'Apprenant';
  const score           = Math.round(Number(certificate.score || 0));
  const modulesValidated = certificate.metadata?.modules_validated || certificate.metadata?.evaluations_passed;

  const s = settings;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-20 print:pt-0 print:bg-white">
      <div className="max-w-4xl mx-auto px-4 py-8 print:px-0 print:py-0">

        {/* Nav */}
        <div className="flex items-center justify-between mb-6 print:hidden">
          <button onClick={() => navigate('/certificates')} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary">
            <ArrowLeft className="w-4 h-4" /> Mes certificats
          </button>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90">
              <Printer className="w-4 h-4" /> Imprimer / PDF
            </button>
            <button onClick={() => handleShare('linkedin')} className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              <Share2 className="w-4 h-4" /> Partager
            </button>
            <button onClick={() => handleShare('twitter')} className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
              <Download className="w-4 h-4" /> Twitter
            </button>
          </div>
        </div>

        {/* Certificat */}
        <div ref={printRef} id="certificate-zone" className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border-4 border-primary/30 print:shadow-none overflow-hidden">

          {/* En-tête */}
          <div className="bg-gradient-to-r from-primary to-primary/70 p-8 text-white text-center">
            <div className="flex flex-col items-center gap-3 mb-3">
              {s.logo_url
                ? <img src={s.logo_url} alt="logo" className="h-20 w-auto max-w-[240px] object-contain drop-shadow-md" />
                : <Award className="w-14 h-14" />}
              <span className="text-2xl font-bold tracking-wide">{s.org_name}</span>
            </div>
            <h2 className="text-2xl font-extrabold tracking-widest uppercase">{s.cert_title}</h2>
          </div>

          {/* Corps */}
          <div className="p-10 text-center">
            <p className="text-gray-500 dark:text-gray-400 text-base mb-4">{s.cert_subtitle}</p>
            <h1 className="text-4xl font-bold text-primary mb-6 border-b-2 border-primary/30 pb-4 inline-block px-8">
              {holderName}
            </h1>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-3">{s.cert_body}</p>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">« {courseTitle} »</h3>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-center gap-1 text-gray-500 dark:text-gray-400 text-sm mb-1">
                  <Calendar className="w-4 h-4" /> Émis le
                </div>
                <p className="font-semibold text-gray-900 dark:text-white text-sm">{formatDate(certificate.issued_at)}</p>
              </div>
              {s.show_score && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Score</div>
                  <p className="font-bold text-primary text-xl">{score}%</p>
                </div>
              )}
              {s.show_modules && modulesValidated && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Modules</div>
                  <p className="font-semibold text-gray-900 dark:text-white text-xl">{modulesValidated}</p>
                </div>
              )}
              {certificate.expires_at && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-gray-500 dark:text-gray-400 text-sm mb-1">Valide jusqu'au</div>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">{formatDate(certificate.expires_at)}</p>
                </div>
              )}
            </div>

            {/* Signatures */}
            <div className="flex flex-col sm:flex-row items-end justify-around border-t border-gray-200 dark:border-gray-700 pt-6 gap-6">
              <div className="text-center">
                {s.signatory_signature_url && (
                  <img
                    src={s.signatory_signature_url}
                    alt="Signature"
                    className="h-14 max-w-[160px] object-contain mx-auto mb-1"
                  />
                )}
                <div className="w-24 border-b-2 border-gray-400 dark:border-gray-500 mx-auto mb-2" />
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <User className="w-3 h-3" /> {s.signatory_title}
                </div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">{s.signatory_name}</p>
              </div>
              <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center">
                <Award className="w-8 h-8 text-white" />
              </div>
              <div className="text-center">
                <div className="w-24 border-b-2 border-gray-400 dark:border-gray-500 mx-auto mb-2" />
                <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                  <BookOpen className="w-3 h-3" /> Plateforme
                </div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">{s.org_name}</p>
              </div>
            </div>
          </div>

          {/* QR code + code de vérification */}
          <div className="bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700 p-5">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div className="flex flex-col items-center gap-1">
                <QRCodeSVG
                  value={`${window.location.origin}/verify?code=${certificate.verification_code}`}
                  size={96} bgColor="#ffffff" fgColor="#000000" level="M" className="rounded"
                />
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Scanner pour vérifier</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 text-sm mb-2">
                  <Shield className="w-4 h-4" /> Code de vérification
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="font-mono text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 text-gray-900 dark:text-white">
                    {certificate.verification_code}
                  </span>
                  <button onClick={copyCode} className="p-2 text-gray-400 hover:text-primary print:hidden" title="Copier">
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                {s.footer_note && <p className="text-xs text-gray-400 mt-2">{s.footer_note}</p>}
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{window.location.origin}/verify</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center print:hidden">
          <button onClick={() => navigate('/courses')} className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-5 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
            <BookOpen className="w-4 h-4" /> Découvrir d'autres cours
          </button>
        </div>
      </div>
    </div>
  );
};

export default CertificateViewer;
